// caselier-collections.js
// Vanilla JS port of CircularGallery (OGL).
// Requires ogl.umd.js loaded first — sets window.ogl (lowercase).

(function () {
  'use strict';

  var ogl = window.ogl;
  if (!ogl) {
    console.warn('[caselier-collections] OGL not found on window.ogl');
    return;
  }

  var mount = document.getElementById('cg-gallery-mount');
  if (!mount) return;

  /* Items injected by Liquid as a JSON <script> tag */
  var dataEl = document.getElementById('cg-gallery-data');
  var items  = [];
  if (dataEl) {
    try { items = JSON.parse(dataEl.textContent.trim()); }
    catch (e) { console.warn('[caselier-collections] Failed to parse gallery data:', e); }
  }
  if (!items.length) return;

  /* Config from data-* on mount div */
  var BEND          = parseFloat(mount.dataset.bend         || '0');
  var TEXT_COLOR    =            mount.dataset.textColor    || '#ffffff';
  var BORDER_RADIUS = parseFloat(mount.dataset.borderRadius || '0.02');
  var SCROLL_SPEED  = parseFloat(mount.dataset.scrollSpeed  || '2');
  var SCROLL_EASE   = parseFloat(mount.dataset.scrollEase   || '0.05');
  var FONT          =            mount.dataset.font         || '700 22px "Space Mono", monospace';
  var IS_TOUCH   = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0 && window.innerWidth <= 768);
  var IS_FIREFOX = navigator.userAgent.indexOf('Firefox') >= 0;
  var TC_SZ    = 14; /* corner bracket px */
  var TC_BW    = 2;  /* border width px */

  var Camera    = ogl.Camera;
  var Mesh      = ogl.Mesh;
  var Plane     = ogl.Plane;
  var Program   = ogl.Program;
  var Renderer  = ogl.Renderer;
  var Texture   = ogl.Texture;
  var Transform = ogl.Transform;

  /* ── Utilities ───────────────────────────────────────────────────────── */

  function lerp(p1, p2, t) { return p1 + (p2 - p1) * t; }

  function debounce(fn, wait) {
    var t;
    return function () {
      var a = arguments, s = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(s, a); }, wait);
    };
  }

  function getFontSize(font) {
    var m = font.match(/(\d+)px/);
    return m ? parseInt(m[1], 10) : 30;
  }

  function createTextTexture(gl, text, font, color) {
    var c = document.createElement('canvas');
    var x = c.getContext('2d');
    var label = text.toUpperCase();
    x.font = font;
    if (x.letterSpacing !== undefined) x.letterSpacing = '0.12em';
    var tw = Math.ceil(x.measureText(label).width);
    var th = Math.ceil(getFontSize(font) * 1.2);
    c.width  = tw + 40;
    c.height = th + 20;
    x.font = font;
    if (x.letterSpacing !== undefined) x.letterSpacing = '0.12em';
    x.fillStyle = color;
    x.textBaseline = 'middle';
    x.textAlign = 'center';
    x.clearRect(0, 0, c.width, c.height);
    x.fillText(label, c.width / 2, c.height / 2);
    var tex = new Texture(gl, { generateMipmaps: false });
    tex.image = c;
    return { texture: tex, width: c.width, height: c.height };
  }

  /* ── Title ───────────────────────────────────────────────────────────── */

  function Title(opts) {
    this.gl        = opts.gl;
    this.plane     = opts.plane;
    this.text      = opts.text;
    this.textColor = opts.textColor || '#545050';
    this.font      = opts.font      || '30px sans-serif';
    this._build();
  }

  Title.prototype._build = function () {
    var r = createTextTexture(this.gl, this.text, this.font, this.textColor);
    var geo = new Plane(this.gl);
    var prg = new Program(this.gl, {
      vertex: [
        'attribute vec3 position; attribute vec2 uv;',
        'uniform mat4 modelViewMatrix; uniform mat4 projectionMatrix;',
        'varying vec2 vUv;',
        'void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }'
      ].join('\n'),
      fragment: [
        'precision highp float; uniform sampler2D tMap; varying vec2 vUv;',
        'void main(){ vec4 c=texture2D(tMap,vUv); if(c.a<0.1)discard; gl_FragColor=c; }'
      ].join('\n'),
      uniforms: { tMap: { value: r.texture } },
      transparent: true
    });
    this.mesh = new Mesh(this.gl, { geometry: geo, program: prg });
    var th = this.plane.scale.y * 0.16;
    this.mesh.scale.set(th * (r.width / r.height), th, 1);
    this.mesh.position.y = -this.plane.scale.y * 0.5 - th * 0.5 - 0.03;
    this.mesh.setParent(this.plane);
  };

  /* ── Media ───────────────────────────────────────────────────────────── */

  function Media(opts) {
    this.extra        = 0;
    this.geometry     = opts.geometry;
    this.gl           = opts.gl;
    this.image        = opts.image;
    this.url          = opts.url || '';
    this.index        = opts.index;
    this.length       = opts.length;
    this.renderer     = opts.renderer;
    this.scene        = opts.scene;
    this.screen       = opts.screen;
    this.text         = opts.text;
    this.viewport     = opts.viewport;
    this.bend         = opts.bend;
    this.textColor    = opts.textColor;
    this.borderRadius = opts.borderRadius || 0;
    this.font         = opts.font;
    this.speed        = 0;
    this.isBefore     = false;
    this.isAfter      = false;
    this._buildShader();
    this._buildMesh();
    this._buildTitle();
    this._resize();
  }

  Media.prototype._buildShader = function () {
    var self = this;
    var tex  = new Texture(this.gl, { generateMipmaps: true });
    this.program = new Program(this.gl, {
      depthTest: false, depthWrite: false,
      vertex: [
        'precision highp float;',
        'attribute vec3 position; attribute vec2 uv;',
        'uniform mat4 modelViewMatrix; uniform mat4 projectionMatrix;',
        'uniform float uTime; uniform float uSpeed;',
        'varying vec2 vUv;',
        'void main(){',
        '  vUv=uv;',
        '  gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);',
        '}'
      ].join('\n'),
      fragment: [
        'precision highp float;',
        'uniform vec2 uImageSizes; uniform vec2 uPlaneSizes;',
        'uniform sampler2D tMap; uniform float uBorderRadius;',
        'varying vec2 vUv;',
        'float roundedBoxSDF(vec2 p,vec2 b,float r){',
        '  vec2 d=abs(p)-b;',
        '  return length(max(d,vec2(0.0)))+min(max(d.x,d.y),0.0)-r;',
        '}',
        'void main(){',
        '  vec2 ratio=vec2(',
        '    min((uPlaneSizes.x/uPlaneSizes.y)/(uImageSizes.x/uImageSizes.y),1.0),',
        '    min((uPlaneSizes.y/uPlaneSizes.x)/(uImageSizes.y/uImageSizes.x),1.0));',
        '  vec2 uv=vec2(vUv.x*ratio.x+(1.0-ratio.x)*0.5,vUv.y*ratio.y+(1.0-ratio.y)*0.5);',
        '  vec4 col=texture2D(tMap,uv);',
        '  float d=roundedBoxSDF(vUv-0.5,vec2(0.5-uBorderRadius),uBorderRadius);',
        '  float alpha=1.0-smoothstep(-0.002,0.002,d);',
        '  gl_FragColor=vec4(col.rgb,alpha);',
        '}'
      ].join('\n'),
      uniforms: {
        tMap:          { value: tex },
        uPlaneSizes:   { value: [0, 0] },
        uImageSizes:   { value: [0, 0] },
        uSpeed:        { value: 0 },
        uTime:         { value: 100 * Math.random() },
        uBorderRadius: { value: this.borderRadius }
      },
      transparent: true
    });
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = this.image;
    img.onload = function () {
      tex.image = img;
      self.program.uniforms.uImageSizes.value = [img.naturalWidth, img.naturalHeight];
    };
  };

  Media.prototype._buildMesh = function () {
    this.plane = new Mesh(this.gl, { geometry: this.geometry, program: this.program });
    this.plane.setParent(this.scene);
  };

  Media.prototype._buildTitle = function () {
    this.title = new Title({
      gl:        this.gl,
      plane:     this.plane,
      renderer:  this.renderer,
      text:      this.text,
      textColor: this.textColor,
      font:      this.font
    });
  };

  Media.prototype.update = function (scroll, direction) {
    this.plane.position.x = this.x - scroll.current - this.extra;

    var x = this.plane.position.x;
    var H = this.viewport.width / 2;

    if (this.bend === 0) {
      this.plane.position.y = 0;
      this.plane.rotation.z = 0;
    } else {
      var B   = Math.abs(this.bend);
      var R   = (H * H + B * B) / (2 * B);
      var ex  = Math.min(Math.abs(x), H);
      var arc = R - Math.sqrt(R * R - ex * ex);
      if (this.bend > 0) {
        this.plane.position.y = -arc;
        this.plane.rotation.z = -Math.sign(x) * Math.asin(ex / R);
      } else {
        this.plane.position.y = arc;
        this.plane.rotation.z = Math.sign(x) * Math.asin(ex / R);
      }
    }

    if (this.title && this.title.mesh) {
      this.title.mesh.rotation.z = -this.plane.rotation.z;
    }

    this.speed = scroll.current - scroll.last;

    var po = this.plane.scale.x / 2;
    var vo = this.viewport.width / 2;
    this.isBefore = this.plane.position.x + po < -vo;
    this.isAfter  = this.plane.position.x - po >  vo;

    if (direction === 'right' && this.isBefore) {
      this.extra -= this.widthTotal;
      this.isBefore = this.isAfter = false;
    }
    if (direction === 'left' && this.isAfter) {
      this.extra += this.widthTotal;
      this.isBefore = this.isAfter = false;
    }
  };

  Media.prototype._resize = function (screen, viewport) {
    if (screen)   this.screen   = screen;
    if (viewport) this.viewport = viewport;
    this.plane.scale.y = this.viewport.height * 0.55;
    this.plane.scale.x = this.plane.scale.y * (3 / 4);
    this.program.uniforms.uPlaneSizes.value = [this.plane.scale.x, this.plane.scale.y];
    this.padding    = this.plane.scale.x * 0.12;
    this.width      = this.plane.scale.x + this.padding;
    this.widthTotal = this.width * this.length;
    this.x          = this.width * this.index;
  };

  /* ── Renderer / camera / scene ───────────────────────────────────────── */

  var renderer = new Renderer({ alpha: true, antialias: true, dpr: Math.min(window.devicePixelRatio || 1, 2) });
  var gl = renderer.gl;
  gl.clearColor(0, 0, 0, 0);
  var canvas = gl.canvas;
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
  mount.appendChild(canvas);

  /* ── Target cursor DOM (disabled on Firefox — WebGL + fixed CSS layer causes GPU stall) */
  var tcEl = null;
  var tcCorners = [];
  if (!IS_FIREFOX) {
    tcEl = document.createElement('div');
    tcEl.id  = 'cg-tc';
    tcEl.innerHTML =
      '<div class="tc-c tc-tl"></div>' +
      '<div class="tc-c tc-tr"></div>' +
      '<div class="tc-c tc-br"></div>' +
      '<div class="tc-c tc-bl"></div>';
    document.body.appendChild(tcEl);
    if (IS_TOUCH) tcEl.classList.add('tc-mobile');
    tcCorners = tcEl.querySelectorAll('.tc-c');
  }
  var tcLastX        = [null, null, null, null];
  var tcLastY        = [null, null, null, null];
  var tcXs           = [0, 0, 0, 0];
  var tcYs           = [0, 0, 0, 0];
  var hoveredMedia   = null;
  var lastHoveredMedia = null; /* stays set while mouse is in gallery — prevents TC jumping to center in card gaps */
  /* Cached canvas rect — invalidated on resize, scroll-adjusted via scrollY */
  var tcR        = { left: 0, top: 0, width: 1, height: 1 };
  var tcRawTop   = 0;
  var tcScrollY0 = 0;
  function refreshCanvasRect() {
    var raw    = canvas.getBoundingClientRect();
    tcR.left   = raw.left;
    tcR.width  = raw.width;
    tcR.height = raw.height;
    tcRawTop   = raw.top;
    tcScrollY0 = window.scrollY || window.pageYOffset || 0;
    tcR.top    = raw.top;
  }

  var camera = new Camera(gl);
  camera.fov = 45;
  camera.position.z = 20;

  var scene    = new Transform();
  var planeGeo = new Plane(gl, { heightSegments: 50, widthSegments: 100 });
  var scroll   = { ease: SCROLL_EASE, current: 0, target: 0, last: 0, position: 0 };

  /* Initial screen + viewport (before medias are created) */
  var screen   = { width: mount.clientWidth || 1, height: mount.clientHeight || 1 };
  renderer.setSize(screen.width, screen.height);
  camera.perspective({ aspect: screen.width / screen.height });

  function computeViewport() {
    var fovRad = (camera.fov * Math.PI) / 180;
    var h = 2 * Math.tan(fovRad / 2) * camera.position.z;
    return { width: h * (screen.width / screen.height), height: h };
  }
  var viewport = computeViewport();

  /* Shuffle so each visitor sees a different starting order */
  for (var _i = items.length - 1; _i > 0; _i--) {
    var _j = Math.floor(Math.random() * (_i + 1));
    var _t = items[_i]; items[_i] = items[_j]; items[_j] = _t;
  }

  /* Duplicate items so the gallery loops seamlessly */
  var allItems = items.concat(items);

  var medias = allItems.map(function (data, i) {
    return new Media({
      geometry:     planeGeo,
      gl:           gl,
      image:        data.image  || '',
      url:          data.url    || '',
      index:        i,
      length:       allItems.length,
      renderer:     renderer,
      scene:        scene,
      screen:       screen,
      text:         data.text   || '',
      viewport:     viewport,
      bend:         BEND,
      textColor:    TEXT_COLOR,
      borderRadius: BORDER_RADIUS,
      font:         FONT
    });
  });

  /* ── Resize ──────────────────────────────────────────────────────────── */

  refreshCanvasRect();

  function onResize() {
    screen = { width: mount.clientWidth || 1, height: mount.clientHeight || 1 };
    renderer.setSize(screen.width, screen.height);
    camera.perspective({ aspect: screen.width / screen.height });
    viewport = computeViewport();
    medias.forEach(function (m) { m._resize(screen, viewport); });
    refreshCanvasRect();
  }
  new ResizeObserver(onResize).observe(mount);

  /* ── Scroll snap ─────────────────────────────────────────────────────── */

  var snapDebounce = debounce(function () {
    if (!medias.length) return;
    var w   = medias[0].width;
    var idx = Math.round(Math.abs(scroll.target) / w);
    var snapped = w * idx;
    scroll.target = scroll.target < 0 ? -snapped : snapped;
  }, 200);

  /* ── Wheel — scoped to gallery (no page-scroll hijack) ──────────────── */

  var isOverCard = false;

  window.addEventListener('wheel', function (e) {
    if (!isOverCard) return;
    e.preventDefault();
    var delta = e.deltaY || e.wheelDelta || e.detail;
    scroll.target += (delta > 0 ? SCROLL_SPEED : -SCROLL_SPEED) * 0.2;
    snapDebounce();
  }, { passive: false });

  /* ── Mouse drag ──────────────────────────────────────────────────────── */

  /* ── Cursor — only show grab when hovering a card ───────────────────── */

  mount.addEventListener('mouseleave', function () {
    isOverCard       = false;
    hoveredMedia     = null;
    lastHoveredMedia = null;
    mount.classList.remove('cg-over-card');
    if (tcEl) tcEl.classList.remove('tc-show');
  });

  if (tcEl && !IS_TOUCH) {
    mount.addEventListener('mouseenter', function () { tcEl.classList.add('tc-show'); });
  } else if (tcEl) {
    tcEl.classList.add('tc-show');
  }

  /* Store latest mouse coords only — actual hit-test runs in RAF (throttles to 60fps) */
  var pendingMX = -1, pendingMY = -1;
  mount.addEventListener('mousemove', function (e) {
    if (isDown) return;
    pendingMX = e.clientX;
    pendingMY = e.clientY;
  });

  var isDown = false, startX = 0, hasDragged = false;

  mount.addEventListener('mousedown', function (e) {
    isDown     = true;
    hasDragged = false;
    startX     = e.clientX;
    scroll.position = scroll.current;
    mount.classList.add('cg-dragging');
  });

  window.addEventListener('mousemove', function (e) {
    if (!isDown) return;
    if (Math.abs(e.clientX - startX) > 4) hasDragged = true;
    scroll.target = scroll.position + (startX - e.clientX) * (SCROLL_SPEED * 0.025);
  });

  window.addEventListener('mouseup', function () {
    if (!isDown) return;
    isDown = false;
    mount.classList.remove('cg-dragging');
    snapDebounce();
  });

  /* ── Touch drag ──────────────────────────────────────────────────────── */

  mount.addEventListener('touchstart', function (e) {
    isDown = true;
    startX = e.touches[0].clientX;
    scroll.position = scroll.current;
  }, { passive: true });

  mount.addEventListener('touchmove', function (e) {
    if (!isDown) return;
    scroll.target = scroll.position + (startX - e.touches[0].clientX) * (SCROLL_SPEED * 0.025);
  }, { passive: true });

  mount.addEventListener('touchend', function () {
    isDown = false;
    snapDebounce();
  });

  /* ── Click — navigate to the clicked card's collection URL ──────────── */

  mount.addEventListener('click', function (e) {
    if (hasDragged) return;
    tcR.top    = tcRawTop - ((window.scrollY || window.pageYOffset || 0) - tcScrollY0);
    var rect    = tcR;
    var ndcX    = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    var ndcY    = 1 - ((e.clientY - rect.top)  / rect.height) * 2;
    var worldX  = ndcX * (viewport.width  / 2);
    var worldY  = ndcY * (viewport.height / 2);

    var best = null, bestDist = Infinity;
    medias.forEach(function (m) {
      var dx = Math.abs(m.plane.position.x - worldX);
      var dy = Math.abs(m.plane.position.y - worldY);
      var hw = m.plane.scale.x / 2;
      var hh = m.plane.scale.y / 2;
      if (dx < hw && dy < hh && dx < bestDist) {
        bestDist = dx;
        best = m;
      }
    });
    if (best && best.url) {
      window.location.href = best.url;
    }
  });

  /* ── Mouse hover — processed once per RAF frame ─────────────────────── */

  function processMouse() {
    if (pendingMX < 0) return;
    var mx = pendingMX, my = pendingMY;
    pendingMX = -1;
    tcR.top = tcRawTop - ((window.scrollY || window.pageYOffset || 0) - tcScrollY0);
    var ndcX   = ((mx - tcR.left) / tcR.width)  * 2 - 1;
    var ndcY   = 1 - ((my - tcR.top) / tcR.height) * 2;
    var worldX = ndcX * (viewport.width  / 2);
    var worldY = ndcY * (viewport.height / 2);
    var found  = null;
    for (var i = 0; i < medias.length; i++) {
      var m = medias[i];
      if (Math.abs(m.plane.position.x - worldX) < m.plane.scale.x / 2 &&
          Math.abs(m.plane.position.y - worldY) < m.plane.scale.y / 2) {
        found = m; break;
      }
    }
    hoveredMedia = found;
    if (found) lastHoveredMedia = found;
    isOverCard   = !!found;
    mount.classList.toggle('cg-over-card', isOverCard);
  }

  /* ── Target cursor helpers ───────────────────────────────────────────── */

  function getCenterMedia() {
    var best = null, bestD = Infinity;
    for (var i = 0; i < medias.length; i++) {
      var d = Math.abs(medias[i].plane.position.x);
      if (d < bestD) { bestD = d; best = medias[i]; }
    }
    return best;
  }

  function updateTC() {
    if (!tcEl) return;
    var target = hoveredMedia || lastHoveredMedia || getCenterMedia();
    if (!target) return;

    tcR.top = tcRawTop - ((window.scrollY || window.pageYOffset || 0) - tcScrollY0);
    var r  = tcR;
    var vw = viewport.width  / 2;
    var vh = viewport.height / 2;
    var hw = target.plane.scale.x / 2;
    var hh = target.plane.scale.y / 2;
    var px = target.plane.position.x;
    var py = target.plane.position.y;

    var left   = r.left + ((px - hw) / vw + 1) / 2 * r.width;
    var right  = r.left + ((px + hw) / vw + 1) / 2 * r.width;
    var top    = r.top  + (1 - (py + hh) / vh) / 2 * r.height;
    var bottom = r.top  + (1 - (py - hh) / vh) / 2 * r.height;

    var bw = TC_BW, cs = TC_SZ;
    tcXs[0] = left  - bw;      tcYs[0] = top    - bw;
    tcXs[1] = right + bw - cs; tcYs[1] = top    - bw;
    tcXs[2] = right + bw - cs; tcYs[2] = bottom + bw - cs;
    tcXs[3] = left  - bw;      tcYs[3] = bottom + bw - cs;

    for (var i = 0; i < 4; i++) {
      var xi = tcXs[i] | 0;
      var yi = tcYs[i] | 0;
      if (xi !== tcLastX[i] || yi !== tcLastY[i]) {
        tcCorners[i].style.transform = 'translate(' + xi + 'px,' + yi + 'px)';
        tcLastX[i] = xi;
        tcLastY[i] = yi;
      }
    }
  }

  /* ── Render loop ─────────────────────────────────────────────────────── */

  var raf = null;
  function update() {
    raf = requestAnimationFrame(update);
    processMouse();
    scroll.current = lerp(scroll.current, scroll.target, scroll.ease);
    var dir = scroll.current > scroll.last ? 'right' : 'left';
    medias.forEach(function (m) { m.update(scroll, dir); });
    renderer.render({ scene: scene, camera: camera });
    scroll.last = scroll.current;
    updateTC();
  }
  update();

  window.addEventListener('beforeunload', function () {
    if (raf) cancelAnimationFrame(raf);
    if (tcEl && tcEl.parentNode) tcEl.parentNode.removeChild(tcEl);
  });

})();
